import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { distinctUntilChanged, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { IArticle } from '../../../models/article.model';
import { IArticleLayout } from '../../../models/layout/article-layout.model';
import { IMeasureUnit } from '../../../models/measure-unit.model';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { ArticleLayoutService } from '../../../services/layout/article-layout.service';
import { RevenueDialogComponent } from './revenue-dialog/revenue-dialog.component';

@Component({
    templateUrl: './article.page.html',
    styleUrls: [ './article.page.css' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArticlePage implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort)
    public sort: MatSort;

    @ViewChild(MatPaginator)
    public paginator: MatPaginator;

    public dataSource: MatTableDataSource<IArticle> = new MatTableDataSource<IArticle>([]);
    public sharedDataState$: Observable<ISharedDataState>;
    public layout: IArticleLayout;
    private destroy$: Subject<void> = new Subject();

    constructor(private mySharedDataFacade: SharedDataFacade,
                private myArticleLayoutService: ArticleLayoutService,
                private myMatDialog: MatDialog) {
    }

    public ngOnInit(): void {
        this.layout = this.myArticleLayoutService.getArticleLayout();
        this.sharedDataState$ = this.mySharedDataFacade.subState();
        this.sharedDataState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ISharedDataState) => s.articleLookup),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: Record<number, IArticle>) => {
                this.dataSource.data = Object.values(data);
            });
        this.sharedDataState$.pipe(
            takeUntil(this.destroy$),
            map((s: ISharedDataState) => s.measureUnitLookup),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        ).subscribe((measureUnitLookup: Record<number, IMeasureUnit>) => {
            this.dataSource.sortingDataAccessor = (article: IArticle, tableColumnName: string): string | number => {
                switch (tableColumnName) {
                    case 'baseMeasureUnitId':
                        return measureUnitLookup[ article.baseMeasureUnitId ]?.name;
                    default:
                        return article[ tableColumnName ];
                }
            };
        });

        void firstValueFrom(this.mySharedDataFacade.fetchArticles());
        void firstValueFrom(this.mySharedDataFacade.fetchMeasureUnits());

    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    public openRevenueDialog(article: IArticle): void {
        this.myMatDialog.open(RevenueDialogComponent, {
            height: '70%',
            width: '40%',
            data: article,
        });
    }
}
